/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOnMatchNotFound';
import { getManager } from '../../registry/Manager';

const ActionOnMatchNotFoundManager: ActionManagerFn<'ActionOnMatchNotFound'> = (
  platform: Platform,
): ActionManager<'ActionOnMatchNotFound'> => {
  return {
    ...baseActionManager(platform, 'ActionOnMatchNotFound'),
    isChildAllowed: (page, item, childType) =>
      getManager(childType).managerType === 'Action',
    defaultValue,
    props: {
      ...baseActionProps,
    },
    replaceableWith: ['ActionOnMatchFound'],
    name: 'On Match Not Found',
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return parent.type === 'ActionMatchRecord';
    },
    description: `**Overview**<br/>
The "On Match Not Found" action is a specialized logic container designed solely to handle the "negative" outcome of an ActionMatchRecord search operation. It acts as the "Else" branch for the record search workflow, executing a defined sequence of child actions only if the parent action iterates through the target records and finds no records that satisfy the defined Match Condition.<br/><br/>
This component is structurally dependent on the ActionMatchRecord component and cannot exist independently. It serves as a wrapper to define the "fallback" or "creation" logic that should occur when a required data entry is missing.

**Key Features**<br/>
* **Strict Parent Scope:** Explicitly restricted via isAllowedInsideParent to function only as a child of ActionMatchRecord. It serves as the exclusive execution path for unsuccessful searches.
* **Logic Grouping:** Functions as a generic container that allows any standard Action component to be added as a child (isChildAllowed).
* **Component Interchangeability:** Explicitly configured to be replaceable with ActionOnMatchFound, allowing developers to easily swap or reorder the logic branches if needed.
* **Zero Configuration:** Inherits all properties from baseActionManager and requires no additional inputs; its execution is entirely determined by the result of the parent's search.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **Auto-Creation:** If a search for a "Client" by email returns no results, use this branch to trigger an ActionCreateRecord to register them as a new user.
* **Error Handling:** If a "Validate Login" match action fails to find a user with the matching credentials, use this branch to show an "Invalid Username or Password" error message.
* **Empty State Handling:** If a search for "Overdue Tasks" returns nothing, use this branch to show a "You are all caught up!" success message.`,
  };
};

export default ActionOnMatchNotFoundManager;
